import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Instagram, Youtube, Facebook, Music, Star, Grid3x3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AddPlatformModal from "@/components/AddPlatformModal";

const iconMap = {
  instagram: Instagram,
  youtube: Youtube,
  facebook: Facebook,
  music: Music, // tiktok
  star: Star,
  grid: Grid3x3,
};

export function PlatformIcon({ icon, className = "w-4 h-4" }) {
  const Ico = iconMap[icon] || Star;
  return <Ico className={className} />;
}

export default function PlatformTabs({ platforms, activeId, setActiveId, counts, onPlatformsChange }) {
  const [addOpen, setAddOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete platform "${name}" and all its links? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/platforms/${id}`);
      toast.success(`Platform "${name}" removed`);
      onPlatformsChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setDeletingId(null); }
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="platform-tabs">
      {platforms.map((p) => {
        const isActive = activeId === p.id;
        const count = counts[p.id] || 0;
        return (
          <motion.button
            key={p.id}
            onClick={() => setActiveId(p.id)}
            whileHover={{ y: -2 }}
            className={`relative flex-shrink-0 group flex items-center gap-2 px-4 py-2.5 rounded border transition-all
              ${isActive
                ? "bg-amber-500/10 border-amber-500 text-amber-300"
                : "bg-slate-900/50 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-600"}`}
            data-testid={`platform-tab-${p.id}`}
            style={isActive ? { boxShadow: "0 0 20px rgba(245,158,11,0.2)" } : undefined}
          >
            <span style={{ color: isActive ? p.color : undefined }}>
              <PlatformIcon icon={p.icon} />
            </span>
            <span className="font-display uppercase font-semibold tracking-wider text-sm">{p.name}</span>
            <span className={`font-mono-vrax text-[10px] px-1.5 py-0.5 rounded ${isActive ? "bg-amber-500/20 text-amber-300" : "bg-slate-800 text-slate-500"}`}>
              {count}
            </span>
            {!p.is_default && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); handleDelete(p.id, p.name); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleDelete(p.id, p.name); }}}
                className={`ml-1 p-1 rounded hover:bg-red-500/20 hover:text-red-400 transition ${deletingId === p.id ? "opacity-50" : ""}`}
                data-testid={`delete-platform-${p.id}`}
                aria-label={`Delete ${p.name}`}
              >
                <Trash2 className="w-3 h-3" />
              </span>
            )}
            {isActive && (
              <motion.div
                layoutId="active-tab-underline"
                className="absolute -bottom-[2px] left-2 right-2 h-[2px] bg-amber-400"
              />
            )}
          </motion.button>
        );
      })}

      <button
        onClick={() => setAddOpen(true)}
        className="flex-shrink-0 btn-ghost-tactical flex items-center gap-2 px-3 py-2.5 rounded"
        data-testid="add-custom-platform-btn"
      >
        <Plus className="w-4 h-4" /> Add Platform
      </button>

      <AddPlatformModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={onPlatformsChange} />
    </div>
  );
}
